import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  prosAssessments: [],
  currentProsAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all PROS Assessments (optionally by patient ID)
export const fetchProsAssessments = createAsyncThunk(
  'prosAssessments/fetchProsAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/pros-assessments/patient/${patientId}`
        : '/pros-assessments';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch PROS assessments'
      );
    }
  }
);

// Fetch a single PROS Assessment by ID
export const fetchProsAssessment = createAsyncThunk(
  'prosAssessments/fetchProsAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/pros-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch PROS assessment'
      );
    }
  }
);

// Create a new PROS Assessment
export const createProsAssessment = createAsyncThunk(
  'prosAssessments/createProsAssessment',
  async (prosAssessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/pros-assessments', prosAssessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create PROS assessment'
      );
    }
  }
);

// Update a PROS Assessment
export const updateProsAssessment = createAsyncThunk(
  'prosAssessments/updateProsAssessment',
  async ({ case_id, prosAssessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/pros-assessments/case/${case_id}`, prosAssessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update PROS assessment'
      );
    }
  }
);

// Delete a PROS Assessment
export const deleteProsAssessment = createAsyncThunk(
  'prosAssessments/deleteProsAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/pros-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete PROS assessment'
      );
    }
  }
);

// ==================== Slice ====================
const prosAssessmentsSlice = createSlice({
  name: 'prosAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentProsAssessment: (state, action) => {
      state.currentProsAssessment = action.payload;
    },
    clearCurrentProsAssessment: (state) => {
      state.currentProsAssessment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchProsAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProsAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.prosAssessments = action.payload;
      })
      .addCase(fetchProsAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchProsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProsAssessment = action.payload;
      })
      .addCase(fetchProsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createProsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.prosAssessments.unshift(action.payload);
      })
      .addCase(createProsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateProsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.prosAssessments.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.prosAssessments[index] = updated;
        }

        if (state.currentProsAssessment?.id === updated.id) {
          state.currentProsAssessment = updated;
        }
      })
      .addCase(updateProsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteProsAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProsAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.prosAssessments = state.prosAssessments.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentProsAssessment?.id === action.payload) {
          state.currentProsAssessment = null;
        }
      })
      .addCase(deleteProsAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentProsAssessment,
  clearCurrentProsAssessment,
} = prosAssessmentsSlice.actions;

export default prosAssessmentsSlice.reducer;